import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { HeartIcon, MagnifyingGlassIcon, CloudIcon, LinkSimpleIcon, FileTextIcon } from '@phosphor-icons/react';
import { SortingState, VisibilityState, RowSelectionState, ColumnSizingState } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import Joyride from 'react-joyride';
import JoyrideTooltip from 'src/components/ui/JoyrideTooltip';
import TanStackTable from 'src/components/ui/TanStackTable';
import Button from 'src/components/ui/Button';
import InputField from 'src/components/ui/Input';
import Checkbox from 'src/components/ui/Checkbox';
import FilteredTableLayout from 'src/components/layouts/FilteredTableLayout';
import WikiFilter from 'src/features/wiki/components/WikiFilter';
import { getDocumentBrowserColumns } from 'src/features/wiki/documentBrowserColumns';
import { useWindowWidth } from 'src/features/wiki/utils';
import type { WikiDocument } from 'src/lib/types';
import { usePostHog } from 'src/lib/posthog';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { RootState } from 'src/app/store/store';
import { setFavouriteDoc, fetchUserFavouriteDocs } from 'src/features/wiki/redux';
import useIsMobile from 'src/hooks/useIsMobile';
import { useDocuments, useInfiniteDocuments, DEFAULT_PAGE_SIZE } from 'src/features/database/hooks/useDocuments';
import { useAggregations } from 'src/features/database/hooks/useAggregations';
import { SortField } from 'src/features/chat/types';
import Loading from './ui/Loading';
import { SpinnerIcon } from '@phosphor-icons/react';

export type DocumentBrowserMode = 'browse' | 'manage';
const EMPTY_DOCUMENTS: WikiDocument[] = [];

export interface DocumentBrowserProps {
  /** Browse mode (Wiki) or Manage mode (Data Library) */
  mode: DocumentBrowserMode;

  /** Actions for table rows (Like, Edit, Delete, etc) - receives (item, filteredData) */
  actions?: any[];

  /** SharePoint document IDs (for Data Library icons) */
  sharePointDocIdSet?: Set<string>;

  /** Scraped document IDs (for Data Library icons) */
  scrapedDocIdSet?: Set<string>;

  /** Show scraped option in origin filter */
  showScrapedOption?: boolean;

  /** Enable origin filter (Data Library) */
  enableOriginFilter?: boolean;

  /** Restrict to managed documents (backend role-based filtering) */
  restrictToManaged?: boolean;

  /** Restrict to globally public documents only */
  publicOnly?: boolean;

  /** Restrict to specific document external IDs */
  documentIds?: string[];

  /** Restrict to documents managed by specific scraper IDs */
  scraperIds?: string[];

  /** Custom filter keys (defaults to category, domain, subDomain, authority) */
  filterKeys?: string[];

  /** Hide category column in table */
  hideCategoryColumn?: boolean;

  /** Hide domain column in table */
  hideDomainColumn?: boolean;

  /** Hide subdomain column in table */
  hideSubDomainColumn?: boolean;

  /** Hide published column in table */
  hidePublishedColumn?: boolean;

  /** Hide the like action */
  hideLikeAction?: boolean;

  /** Hide the "Liked Documents" filter button */
  hideLikedDocumentsFilter?: boolean;

  /** Show uploaded column in table (Data Library) */
  showUploadedColumn?: boolean;

  /** Enable sorting dropdown (Wiki only) */
  enableSorting?: boolean;

  /** Custom CSS class for the filter panel */
  filterClassName?: string;

  // Controlled state from parent (URL or local)
  filters?: Record<string, string[]>;
  onFilterChange?: (key: string, values: string[]) => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  sorting?: SortingState;
  onSortingChange?: (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => void;
  showLikedOnly?: boolean;
  onToggleLiked?: (value: boolean) => void;

  // Selection
  multiselect?: boolean;
  selectedItems?: (string | number)[];
  onSelectionChange?: (items: (string | number)[], allDocuments: WikiDocument[]) => void;

  // Handlers
  onRowClick?: (item: WikiDocument) => void;

  /** Callback when filtered data changes */
  onFilteredDataChange?: (data: WikiDocument[]) => void;

  /** Callback when the backend-reported total document count changes */
  onTotalCountChange?: (count: number) => void;

  /** Hide the total document count shown in the header row */
  hideDocumentCount?: boolean;

  /** Whether to toggle selection when a row is clicked */
  selectionMode?: 'none' | 'toggle';

  // Initial state for uncontrolled mode
  initialFilters?: Record<string, string[]>;

  // Customization
  hideFilterPanel?: boolean;
  itemsPerPage?: number;
  /**
   * Override the minimum height of the inner table. Defaults to "36rem" which
   * prevents layout shift as rows load. Pass a smaller value (e.g. 0) when the
   * browser is embedded in a constrained container and you'd rather the table
   * size to its content instead of forcing the parent to scroll.
   */
  tableMinHeight?: string | number;

  /**
   * Disable the internal `overflow-y-auto` wrapper. Use when the host is
   * responsible for constraining the browser height and should not see an
   * internal scrollbar appear (e.g. inline panels with dynamic itemsPerPage).
   */
  disableInternalScroll?: boolean;

  /** Load further pages as the list scrolls, and pin the column header. */
  infinite?: boolean;
}

export default function DocumentBrowser({
  mode,
  actions = [],
  sharePointDocIdSet,
  scrapedDocIdSet,
  showScrapedOption = false,
  enableOriginFilter = false,
  restrictToManaged = false,
  publicOnly = false,
  documentIds = [],
  scraperIds = [],
  filterKeys = ['category', 'domain', 'subDomain', 'authority'],
  hideCategoryColumn = false,
  hideDomainColumn = false,
  hideSubDomainColumn = false,
  hidePublishedColumn = false,
  hideLikeAction = false,
  hideLikedDocumentsFilter = false,
  showUploadedColumn = false,
  enableSorting = true,
  filterClassName,
  // Controlled state
  filters: externalFilters,
  onFilterChange: externalOnFilterChange,
  searchQuery: externalSearchQuery,
  onSearchChange: externalOnSearchChange,
  page: externalPage,
  onPageChange: externalOnPageChange,
  sorting: externalSorting,
  onSortingChange: externalOnSortingChange,
  showLikedOnly: externalShowLikedOnly,
  onToggleLiked: externalOnToggleLiked,
  multiselect = true,
  selectedItems: externalSelectedItems,
  onSelectionChange: externalOnSelectionChange,
  onRowClick,
  onFilteredDataChange,
  onTotalCountChange,
  hideDocumentCount = false,
  selectionMode = 'none',
  initialFilters = {},
  // Customization
  hideFilterPanel = false,
  itemsPerPage = DEFAULT_PAGE_SIZE,
  tableMinHeight = '36rem',
  disableInternalScroll = false,
  infinite = false
}: DocumentBrowserProps) {
  const windowWidth = useWindowWidth();
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // Internal states for optional props (uncontrolled mode)
  const [internalFilters, setInternalFilters] = useState<Record<string, string[]>>(initialFilters);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [internalPage, setInternalPage] = useState(1);
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalShowLikedOnly, setInternalShowLikedOnly] = useState(false);
  const [internalSelectedItems, setInternalSelectedItems] = useState<(string | number)[]>([]);

  // Use external state if provided, otherwise fallback to internal
  const filters = externalFilters !== undefined ? externalFilters : internalFilters;
  const onFilterChange =
    externalOnFilterChange || ((key, values) => setInternalFilters((prev) => ({ ...prev, [key]: values })));
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const onSearchChange = externalOnSearchChange || setInternalSearchQuery;
  const page = externalPage !== undefined ? externalPage : internalPage;
  const onPageChange = externalOnPageChange || setInternalPage;
  const sorting = externalSorting !== undefined ? externalSorting : internalSorting;
  const onSortingChange = externalOnSortingChange || setInternalSorting;
  const showLikedOnly = externalShowLikedOnly !== undefined ? externalShowLikedOnly : internalShowLikedOnly;
  const onToggleLiked = externalOnToggleLiked || setInternalShowLikedOnly;
  const selectedItems = externalSelectedItems !== undefined ? externalSelectedItems : internalSelectedItems;
  const onSelectionChange = externalOnSelectionChange || ((items) => setInternalSelectedItems(items));

  // Convert TanStack sorting state to API params
  // Map frontend column IDs to backend field names
  const columnIdToSortField = (columnId: string): SortField => {
    const mapping: Record<string, SortField> = {
      title: 'title',
      category: 'category',
      domain: 'domain',
      published: 'publication_date'
    };
    return mapping[columnId] || 'title';
  };

  const sortBy: SortField | null = sorting[0]?.id ? columnIdToSortField(sorting[0].id) : null;
  const reverse: boolean = sorting[0]?.desc ?? false;

  const documentQuery = {
    pageSize: infinite ? Math.max(itemsPerPage, 40) : itemsPerPage,
    search: searchQuery,
    sortBy,
    reverse,
    categories: filters.category || [],
    domains: filters.domain || [],
    subdomains: filters.subDomain || [],
    authorities: filters.authority || [],
    documentIds,
    scraperIds,
    favouritesOnly: showLikedOnly,
    sources: filters.origin || [],
    restrictToManaged: restrictToManaged,
    publicOnly: publicOnly
  };
  const { data: pagedDocuments, isLoading: pagedLoading } = useDocuments({
    ...documentQuery,
    page,
    enabled: !infinite
  });
  const {
    data: infiniteDocuments,
    isLoading: infiniteLoading,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isRefetchError,
    fetchNextPage,
    refetch
  } = useInfiniteDocuments({ ...documentQuery, enabled: infinite });
  const documentsData = useMemo(() => {
    if (!infinite) return pagedDocuments;
    const pages = infiniteDocuments?.pages;
    const lastPage = pages?.at(-1);
    if (!pages || !lastPage) return undefined;
    return { ...lastPage, references: pages.flatMap((pageData) => pageData.references) };
  }, [infinite, infiniteDocuments, pagedDocuments]);
  const fetchIsLoading = infinite ? infiniteLoading && !infiniteDocuments : pagedLoading;

  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = endRef.current;
    if (!target || !infinite || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      { root: null, rootMargin: '240px' }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    infinite,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    documentsData?.references.length
  ]);
  const { data: aggregationsData } = useAggregations({
    search: searchQuery,
    categories: filters.category || [],
    domains: filters.domain || [],
    subdomains: filters.subDomain || [],
    authorities: filters.authority || [],
    documentIds,
    scraperIds,
    favouritesOnly: showLikedOnly,
    sources: filters.origin || [],
    restrictToManaged: restrictToManaged,
    publicOnly: publicOnly
  });
  const documents = documentsData?.references ?? EMPTY_DOCUMENTS;
  const isLoading = fetchIsLoading;

  // Calculate total page count for server-side pagination
  const pageCount = documentsData?.total_pages;

  const { userFavouriteDocs } = useAppSelector((state: RootState) => state.wiki);

  // Tour state
  const [showSortTour, setShowSortTour] = useState(false);

  useEffect(() => {
    onFilteredDataChange?.(documents);
  }, [documents, onFilteredDataChange]);

  useEffect(() => {
    if (documentsData?.total_count !== undefined) {
      onTotalCountChange?.(documentsData.total_count);
    }
  }, [documentsData?.total_count, onTotalCountChange]);

  // Add icons to the data for display
  const finalTableData = useMemo(() => {
    return documents.map((doc) => {
      let icon: React.ReactNode = <FileTextIcon size={20} weight="thin" className="text-content-subtle" />;
      if (mode === 'manage') {
        const isSp = sharePointDocIdSet?.has(String(doc.id)) || false;
        const isScraped = scrapedDocIdSet?.has(String(doc.id)) || false;
        if (isScraped) icon = <LinkSimpleIcon size={18} weight="light" className="text-primary-blue" />;
        else if (isSp) icon = <CloudIcon size={18} weight="light" className="text-primary-blue" />;
      }
      return { ...doc, icon };
    });
  }, [documents, mode, sharePointDocIdSet, scrapedDocIdSet]);

  // Local UI state
  const [useSuggestedSort, setUseSuggestedSort] = useState(enableSorting);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [hoveredRow, setHoveredRow] = useState<string | number | null>(null);

  // Convert selectedItems to rowSelection format for TanStack Table
  const rowSelection = useMemo(() => {
    const selection: RowSelectionState = {};
    selectedItems.forEach((id) => {
      selection[id.toString()] = true;
    });
    return selection;
  }, [selectedItems]);

  // Sync column visibility
  useEffect(() => {
    const newVisibility: VisibilityState = {
      title: true,
      category: !hideCategoryColumn,
      domain: !hideDomainColumn,
      subDomain: !hideSubDomainColumn,
      published: !hidePublishedColumn,
      uploaded: showUploadedColumn
    };
    if (hideCategoryColumn) newVisibility.category = false;
    if (hideDomainColumn) newVisibility.domain = false;
    if (hideSubDomainColumn) newVisibility.subDomain = false;
    if (hidePublishedColumn) newVisibility.published = false;

    if (windowWidth < 1600) newVisibility.subDomain = false;
    if (windowWidth < 1400) newVisibility.domain = false;
    if (windowWidth < 1200) newVisibility.category = false;
    if (showUploadedColumn && windowWidth < 1100) newVisibility.uploaded = false;
    if (windowWidth < 1000) newVisibility.published = false;
    if (windowWidth < 900) {
      newVisibility.title = true;
      newVisibility.published = true;
      newVisibility.category = false;
      newVisibility.domain = false;
      newVisibility.subDomain = false;
      newVisibility.uploaded = false;
    }
    if (windowWidth < 500) {
      newVisibility.title = true;
      newVisibility.published = false;
    }
    setColumnVisibility(newVisibility);
    setColumnSizing({});
  }, [windowWidth, hideCategoryColumn, hideDomainColumn, hideSubDomainColumn, hidePublishedColumn, showUploadedColumn]);

  // Handle suggested sort toggle
  useEffect(() => {
    if (sorting.length > 0 && useSuggestedSort) {
      setUseSuggestedSort(false);
    } else if (sorting.length === 0 && !useSuggestedSort && enableSorting) {
      setUseSuggestedSort(true);
    }
  }, [sorting, useSuggestedSort, enableSorting]);

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      onSortingChange(updaterOrValue);
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      if (newSorting.length > 0) {
        const sortInfo = newSorting[0];
        const eventName = mode === 'browse' ? 'wiki_column_sort_used' : 'data_library_column_sort_used';
        posthog?.capture(eventName, { columnId: sortInfo.id, direction: sortInfo.desc ? 'desc' : 'asc' });
      }
    },
    [onSortingChange, sorting, mode, posthog]
  );

  const handleSuggestedSortChange = useCallback(
    (checked: boolean) => {
      setUseSuggestedSort(checked);
      if (checked) {
        onSortingChange([]);
        posthog?.capture('wiki_suggested_sort_enabled');
      } else {
        posthog?.capture('wiki_suggested_sort_disabled');
      }
    },
    [onSortingChange, posthog]
  );

  const handleSuggestedSortClick = useCallback(() => {
    if (useSuggestedSort) {
      setShowSortTour(true);
      posthog?.capture('wiki_suggested_sort_help_shown');
    }
  }, [useSuggestedSort, posthog]);

  // Search debounce
  const searchEventTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handleSearch = useCallback(
    (value: string) => {
      onSearchChange(value);
      if (searchEventTimerRef.current) clearTimeout(searchEventTimerRef.current);
      searchEventTimerRef.current = setTimeout(() => {
        if (value.trim()) {
          const eventName = mode === 'browse' ? 'wiki_search_used' : 'data_library_search_used';
          posthog?.capture(eventName, { query: value });
        }
      }, 1000);
    },
    [onSearchChange, mode, posthog]
  );

  useEffect(() => {
    return () => {
      if (searchEventTimerRef.current) clearTimeout(searchEventTimerRef.current);
    };
  }, []);

  const handleToggleLikedFilter = useCallback(() => {
    onToggleLiked(!showLikedOnly);
    const eventName = mode === 'browse' ? 'wiki_liked_filter_toggled' : 'data_library_liked_filter_toggled';
    posthog?.capture(eventName, { enabled: !showLikedOnly });
  }, [showLikedOnly, onToggleLiked, mode, posthog]);

  // Handle row selection changes from TanStack Table
  const handleRowSelectionChange = useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newRowSelection = typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      // Convert rowSelection back to array of IDs
      const newSelectedIds = Object.keys(newRowSelection).filter((key) => newRowSelection[key]);

      // Notify parent with the new selection
      onSelectionChange?.(newSelectedIds, documents);
    },
    [rowSelection, onSelectionChange, documents]
  );

  const handleRowClickInternal = useCallback(
    (item: WikiDocument) => {
      if (selectionMode === 'toggle') {
        const id = item.id;
        const newSelection = selectedItems.includes(id)
          ? selectedItems.filter((i) => i !== id)
          : [...selectedItems, id];
        onSelectionChange?.(newSelection, documents);
      }
      onRowClick?.(item);
    },
    [selectionMode, selectedItems, onSelectionChange, documents, onRowClick]
  );

  // Columns
  const columns = useMemo(() => {
    const hasIcons = mode === 'manage' || finalTableData.some((doc) => doc.icon);
    return getDocumentBrowserColumns({
      multiselect,
      hasIcons,
      hideCategoryColumn,
      showUploadedColumn,
      hoveredRow,
      isMobile
    });
  }, [mode, finalTableData, multiselect, hideCategoryColumn, showUploadedColumn, hoveredRow, isMobile]);

  const getRowId = useCallback((row: WikiDocument) => {
    return row.id;
  }, []);

  // Actions
  const actionsWithLike = useMemo(() => {
    if (hideLikeAction || !Array.isArray(userFavouriteDocs)) return actions;
    const likeAction = {
      label: (item: WikiDocument) => (userFavouriteDocs.includes(item.id) ? 'Unlike' : 'Like'),
      icon: HeartIcon,
      iconProps: (item: WikiDocument) => {
        const isLiked = userFavouriteDocs.includes(item.id);
        return { weight: isLiked ? ('fill' as const) : ('light' as const), color: isLiked ? '#ef4444' : '#6b7280' };
      },
      onClick: (item: WikiDocument) =>
        dispatch(setFavouriteDoc(item.id, !userFavouriteDocs.includes(item.id))).then(() => {
          dispatch(fetchUserFavouriteDocs({ suppressError: false }));
          // Invalidate query caches so filters reflect the change immediately
          queryClient.invalidateQueries({ queryKey: ['documents'] });
          queryClient.invalidateQueries({ queryKey: ['aggregations'] });
        })
    };
    return [likeAction, ...actions];
  }, [userFavouriteDocs, actions, dispatch, queryClient]);

  const wrappedActions = useMemo(() => {
    return actionsWithLike.map((action) => ({
      ...action,
      onClick: (item: WikiDocument) => action.onClick(item, finalTableData),
      disabled: action.disabled ? (item: WikiDocument) => action.disabled(item, finalTableData) : undefined
    }));
  }, [actionsWithLike, finalTableData]);

  const activeFilters = filterKeys.filter((key) => filters[key]?.length > 0);
  const hasActiveFilters = activeFilters.length > 0 || searchQuery.trim() !== '' || showLikedOnly;
  const resetFilters = useCallback(() => {
    for (const key of filterKeys) {
      if ((filters[key] || []).length > 0) onFilterChange(key, []);
    }
    if (searchQuery) onSearchChange('');
    if (showLikedOnly) onToggleLiked(false);
  }, [filterKeys, filters, onFilterChange, searchQuery, onSearchChange, showLikedOnly, onToggleLiked]);

  const renderFilter = () => (
    <WikiFilter
      filters={filters}
      onFilterChange={onFilterChange}
      aggregations={aggregationsData}
      className={filterClassName}
      originEnabled={enableOriginFilter}
      showScrapedOption={showScrapedOption}
      hideCategoryWhenSingle={mode === 'manage'}
      mode={mode}
    />
  );

  const [layoutKey, setLayoutKey] = useState(0);

  // Force layout recalculation when window width changes
  useEffect(() => {
    setLayoutKey((prev) => prev + 1);
  }, [windowWidth]);

  const documentCountLabel = documentsData?.total_count
    ? `${documentsData.total_count} ${documentsData.total_count === 1 ? 'Document' : 'Documents'}`
    : `${finalTableData.length} ${finalTableData.length === 1 ? 'Document' : 'Documents'}`;
  const isToolbarFilters = !hideFilterPanel;
  const listFooter =
    infinite && finalTableData.length > 0 ? (
      <div ref={endRef} className="flex justify-center py-3 text-sm text-content-subtlest">
        {isFetchingNextPage ? (
          <span className="inline-flex items-center gap-2">
            <SpinnerIcon size={16} className="animate-spin" />
            Loading…
          </span>
        ) : isFetchNextPageError ? (
          <Button variant="ghost" size="sm" type="button" onClick={() => fetchNextPage()}>
            Retry
          </Button>
        ) : isRefetchError ? (
          <Button variant="ghost" size="sm" type="button" onClick={() => refetch()}>
            Retry
          </Button>
        ) : !hasNextPage ? (
          <span>End of list</span>
        ) : null}
      </div>
    ) : null;
  const table = isLoading ? (
    <Loading text="Loading wiki documents..." className="h-full min-h-[12rem] w-full" />
  ) : (
    <TanStackTable
      data={finalTableData}
      columns={columns}
      onRowClick={handleRowClickInternal}
      actions={wrappedActions}
      contextMenu
      itemsPerPage={infinite ? Math.max(finalTableData.length, 1) : itemsPerPage}
      stickyHeader={infinite}
      multiselect={multiselect}
      enableSorting={true}
      enableResizing={true}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      rowSelection={multiselect ? rowSelection : undefined}
      onRowSelectionChange={multiselect ? handleRowSelectionChange : undefined}
      columnSizing={columnSizing}
      onColumnSizingChange={setColumnSizing}
      getRowId={getRowId}
      onHoveredRowChange={setHoveredRow}
      currentPage={infinite ? 1 : page}
      onPageChange={infinite ? () => {} : onPageChange}
      pageCount={infinite ? 1 : pageCount}
      totalItems={documentsData?.total_count}
      fluidColumnId="title"
      minHeight={infinite ? 0 : tableMinHeight}
    />
  );
  const tableWithFooter = (
    <>
      {table}
      {listFooter}
    </>
  );

  return (
    <div className="w-full flex min-w-0">
      <div
        className={`flex w-full min-w-0 ${
          isToolbarFilters ? '@container flex-col gap-2' : 'max-h-full overflow-hidden flex-col gap-6 md:flex-row'
        }`}
        key={layoutKey}
      >
        <div className="flex-1 flex flex-col min-w-0 min-h-0 md:rounded-lg">
          {isToolbarFilters ? (
            <FilteredTableLayout
              toolbar={
                <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
                  <div className="search-bar min-w-0 w-full max-w-64 flex-1 basis-full sm:basis-auto">
                    <InputField
                      icon={MagnifyingGlassIcon}
                      placeholder="Search documents..."
                      state="default"
                      type="search"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </div>
                  {renderFilter()}
                  {!hideLikedDocumentsFilter && (
                    <Button
                      variant="outline"
                      size="md"
                      icon={HeartIcon}
                      iconProps={{
                        weight: showLikedOnly ? 'fill' : 'regular',
                        color: showLikedOnly ? '#ef4444' : undefined
                      }}
                      onClick={handleToggleLikedFilter}
                      aria-label="Liked documents"
                      className="liked-documents-button shrink-0 max-md:[&_[data-slot=button-label]]:sr-only"
                    >
                      Liked Documents
                    </Button>
                  )}
                </div>
              }
              trailing={
                enableSorting ? (
                  <label
                    className={`flex items-center gap-2 text-sm text-content-default cursor-pointer select-none whitespace-nowrap ${useSuggestedSort ? 'opacity-50' : ''}`}
                    onClick={handleSuggestedSortClick}
                  >
                    <Checkbox
                      checked={useSuggestedSort}
                      onChange={(e) => handleSuggestedSortChange(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>Suggested Sort</span>
                  </label>
                ) : undefined
              }
              count={documentsData?.total_count ?? finalTableData.length}
              itemName={{ singular: 'document', plural: 'documents' }}
              hasActiveFilters={hasActiveFilters}
              onResetFilters={resetFilters}
            >
              <div
                className={`documents-table w-full min-w-0 flex-1 min-h-0 ${
                  disableInternalScroll || infinite ? '' : 'overflow-y-auto custom-scrollbar'
                }`}
              >
                {tableWithFooter}
              </div>
            </FilteredTableLayout>
          ) : (
            <>
              <div className="flex w-full min-w-0 items-center gap-2 pb-3">
                <div className="search-bar min-w-0 w-full sm:flex-1">
                  <InputField
                    icon={MagnifyingGlassIcon}
                    placeholder="Search documents..."
                    state="default"
                    type="search"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
                {!hideLikedDocumentsFilter && (
                  <Button
                    variant="outline"
                    size="md"
                    icon={HeartIcon}
                    iconProps={{
                      weight: showLikedOnly ? 'fill' : 'regular',
                      color: showLikedOnly ? '#ef4444' : undefined
                    }}
                    onClick={handleToggleLikedFilter}
                    aria-label="Liked documents"
                    className="liked-documents-button shrink-0 max-md:[&_[data-slot=button-label]]:sr-only"
                  >
                    Liked Documents
                  </Button>
                )}
                {enableSorting && (
                  <div className="flex flex-row gap-2 items-center suggested-sort-checkbox">
                    <label
                      className={`flex items-center gap-2 text-sm text-content-default cursor-pointer select-none whitespace-nowrap ${useSuggestedSort ? 'opacity-50' : ''}`}
                      onClick={handleSuggestedSortClick}
                    >
                      <Checkbox
                        checked={useSuggestedSort}
                        onChange={(e) => handleSuggestedSortChange(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>Suggested Sort</span>
                    </label>
                  </div>
                )}
                {!hideDocumentCount && (
                  <span className="ml-auto shrink-0 text-sm font-medium text-content-subtle">{documentCountLabel}</span>
                )}
              </div>
              <div
                className={`documents-table w-full min-w-0 flex-1 min-h-0 ${
                  disableInternalScroll || infinite ? '' : 'overflow-y-auto custom-scrollbar'
                }`}
              >
                {tableWithFooter}
              </div>
            </>
          )}
        </div>
      </div>

      <Joyride
        steps={[
          {
            target: '.documents-table thead th:nth-child(2)',
            content: 'Click on any column header to sort the table by that column.',
            placement: 'bottom',
            disableBeacon: true
          }
        ]}
        run={showSortTour}
        continuous={false}
        showProgress={false}
        showSkipButton={false}
        callback={(data) => {
          if (data.status === 'finished' || data.status === 'skipped') setShowSortTour(false);
        }}
        styles={{ options: { primaryColor: '#222B6E', zIndex: 1000 } }}
        locale={{ last: 'Got it' }}
        tooltipComponent={JoyrideTooltip}
      />
    </div>
  );
}
